import * as React from 'react';
import { useLocation } from 'react-router-dom';
import {
  getGetApiTenantsTenantSlugCustomerSettingsQueryKey,
  useGetApiTenantsTenantSlugCustomerSettings
} from '@cdf/api-client-react';
import type {
  CustomerSkinEffective,
  ResolvedHeaderBranding,
  CustomerSkinPalette,
  CustomerSkinTheme,
  HomeLayout,
  NavigationLayout
} from '@cdf/api-client-react';

import { useUser } from '@/components/auth/user-provider';
import { useTheme } from '@/components/theme/theme-provider';
import { defaultEntity, selectedEntity } from '@/features/tenancy/context-navigation';
import { tenantSlugFromPath } from '@/features/tenancy/urls';
import { brandScope, readableForeground, skinFontStack, skinTheme, skinTokens, type BrandScope, type SkinThemeName } from '@/lib/customer-skin';
import { BrowserIdentityProvider, type BrowserIdentity } from '@/lib/browser-identity';
import type { LayoutAuthority } from '@/lib/managed-mode';
import { EntityPrivacyProvider } from '@/lib/entity-privacy';
import { TenantCheckInProvider } from '@/lib/tenant-check-in';

export type CustomerLogo = { dataUrl: string; altText: string } | null;

/** Configured header surface for the active theme. */
export interface CustomerHeaderSurface {
  background: string;
  foreground: string;
}

export interface CustomerSkinContextValue {
  /** Uploaded scoped logo, or null when the customer has none. */
  logo: CustomerLogo;
  headerBranding: ResolvedHeaderBranding | null;
  showNotificationIcon: boolean;
  contextKey: string;
  sidebarHiddenByDefault: boolean;
  sidebarSettingsReady: boolean;
  homepage: 'home-1' | 'home-2' | null;
  homepageError: boolean;
  retryHomepage: () => void;
  activeEntitySlug: string | null;
  /** Resolved effective skin; null until settings load. */
  skin: CustomerSkinEffective | null;
  /** Active colour theme. */
  theme: SkinThemeName;
  /** Semantic colours for the active theme. */
  themeValues: CustomerSkinTheme;
  /** Named palette entries that carry a value. */
  palette: CustomerSkinPalette;
  /** Configured header surface for the active theme; null keeps the app header. */
  header: CustomerHeaderSurface | null;
  /** Bundled font stack when the skin requests one; null keeps the app default. */
  fontFamily: string | null;
  /** Resolved platform, tenant or entity navigation layout. */
  navigationLayout: NavigationLayout | null;
  /** Resolved Home section order and visibility. */
  homeLayout: HomeLayout | null;
  /** Server-derived layout edit rights for this selected entity. */
  layoutAuthority: LayoutAuthority | null;
}

const emptyContext: CustomerSkinContextValue = {
  logo: null,
  headerBranding: null,
  showNotificationIcon: false,
  contextKey: '',
  sidebarHiddenByDefault: false,
  sidebarSettingsReady: true,
  homepage: null,
  homepageError: false,
  retryHomepage: () => {},
  activeEntitySlug: null,
  skin: null,
  theme: 'light',
  themeValues: {},
  palette: {},
  header: null,
  fontFamily: null,
  navigationLayout: null,
  homeLayout: null,
  layoutAuthority: null
};

const CustomerSkinContext = React.createContext<CustomerSkinContextValue>(emptyContext);

/** Generic skin context for renderers and decorative components. */
export function useCustomerSkin(): CustomerSkinContextValue {
  return React.useContext(CustomerSkinContext);
}

export function useCustomerLogo(): CustomerLogo {
  return useCustomerSkin().logo;
}

/** Resolved "Where the brand applies" switches for the signed-in customer. */
export function useBrandScope(): BrandScope {
  return brandScope(useCustomerSkin().skin);
}

/** Scoped under EntityCacheBoundary so an account/entity switch cannot reuse another customer's query. */
export function CustomerBrandProvider({ children }: { children: React.ReactNode }) {
  const user = useUser();
  const { pathname } = useLocation();
  const { resolvedTheme } = useTheme();
  const entity = selectedEntity(user?.tenants ?? [], pathname) ??
    (tenantSlugFromPath(pathname) ? undefined : defaultEntity(user?.tenants ?? []));
  const query = useGetApiTenantsTenantSlugCustomerSettings(entity?.slug ?? '', {
    query: {
      queryKey: [...getGetApiTenantsTenantSlugCustomerSettingsQueryKey(entity?.slug ?? ''), user?.appUserId, user?.viewAsRole, entity?.tenantId, entity?.id],
      enabled: Boolean(entity?.slug && entity.status === 'active'),
      retry: false,
      refetchOnWindowFocus: true,
      staleTime: 30_000
    }
  });
  const settings = entity?.status === 'active' && query.data?.entityId === entity.id && query.data?.tenantId === entity.tenantId ? query.data : null;

  const value = React.useMemo<CustomerSkinContextValue>(() => {
    const skin = settings?.skin.effective ?? null;
    const themeValues = skinTheme(skin, resolvedTheme);
    const headerBackground = themeValues.headerBackground ?? null;
    const headerForeground =
      themeValues.headerForeground ?? (headerBackground ? readableForeground(headerBackground) : null);
    return {
      headerBranding: settings?.headerBranding ?? null,
      showNotificationIcon: Boolean(settings) && (settings?.headerDisplay?.showNotificationIcon ?? true),
      contextKey: [user?.appUserId, user?.viewAsRole, entity?.tenantId, entity?.id].join(':'),
      sidebarHiddenByDefault: settings?.sidebarDisplay?.hiddenByDefault ?? false,
      sidebarSettingsReady: !entity || entity.status !== 'active' || Boolean(settings) || query.isError,
      homepage: !entity ? (tenantSlugFromPath(pathname) ? null : 'home-1') : query.isError || query.isPlaceholderData ? null : settings?.defaultHomepage ?? (settings ? 'home-1' : null),
      homepageError: Boolean(entity && query.isError),
      retryHomepage: () => { void query.refetch(); },
      activeEntitySlug: entity?.status === 'active' ? entity.slug : null,
      logo: settings?.logo ? { dataUrl: settings.logo.dataUrl, altText: settings.logo.altText } : null,
      skin,
      theme: resolvedTheme,
      themeValues,
      palette: skin?.palette ?? {},
      header: headerBackground && headerForeground ? { background: headerBackground, foreground: headerForeground } : null,
      fontFamily: skinFontStack(skin?.typography),
      navigationLayout: settings?.navigationLayout.effective ?? null,
      homeLayout: settings?.homeLayout.effective ?? null,
      layoutAuthority: settings ? {
        entityId: settings.entityId,
        canManageTenant: settings.canManageTenant,
        canManageEntity: settings.canManageEntity,
        navigationEntityUnlocked: settings.navigationLayout.allowEntityOverride,
        homeEntityUnlocked: settings.homeLayout.allowEntityOverride
      } : null
    };
  }, [settings, resolvedTheme, user?.appUserId, user?.viewAsRole, entity?.tenantId, entity?.id, entity?.slug, entity?.status, pathname, query.isError, query.isPlaceholderData, query.refetch]);

  // Write the effective skin to the document root so descendants and portalled
  // surfaces share the tokens, and remove every value this provider wrote before
  // the next theme, entity or account applies.
  const appliedTokens = React.useRef<string[]>([]);
  const viewport = usePhoneViewport();
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    if (value.skin?.surfaces && Object.values(value.skin.surfaces).some(item => item != null)) root.dataset.customerSurfaces = '';
    else delete root.dataset.customerSurfaces;
    for (const name of appliedTokens.current) {
      root.style.removeProperty(name);
    }
    const legacyPrimary = value.theme === 'dark' ? settings?.effective.darkPrimary : settings?.effective.lightPrimary;
    const legacyForeground = value.theme === 'dark' ? settings?.foreground.darkPrimary : settings?.foreground.lightPrimary;
    const tokens = skinTokens({ ...value, viewport }, { primary: legacyPrimary, foreground: legacyForeground });
    appliedTokens.current = Object.keys(tokens);
    for (const [name, token] of Object.entries(tokens)) {
      root.style.setProperty(name, token);
    }
    return () => {
      for (const name of appliedTokens.current) {
        root.style.removeProperty(name);
      }
      appliedTokens.current = [];
      delete root.dataset.customerSurfaces;
    };
  }, [value, settings, viewport]);

  // The tab shows the customer name and logo only while the `browserIdentity`
  // switch is on; signing out clears the entity and restores the app identity.
  const entityName = entity?.name ?? null;
  const browserIdentity = React.useMemo<BrowserIdentity | null>(() => (
    settings && entityName && brandScope(value.skin).browserIdentity
      ? { name: entityName, iconHref: value.logo?.dataUrl ?? null }
      : null
  ), [settings, entityName, value.skin, value.logo]);

  return <BrowserIdentityProvider identity={browserIdentity}><CustomerSkinContext.Provider value={value}>
    <EntityPrivacyProvider privacy={settings?.privacy} ready={query.isSuccess && !query.isFetching && !query.isPlaceholderData}
      authority={JSON.stringify([user?.appUserId, user?.viewAsRole, user?.capabilities, user?.tenants, user?.groupAccess, user?.isPlatformAdmin])}>
      <TenantCheckInProvider tenantSlug={entity?.slug ?? ''} entityId={entity?.id} tenantId={entity?.tenantId}
        active={Boolean(user && !user.viewAsRole && entity?.status === 'active')}
        authority={JSON.stringify([user?.appUserId, user?.viewAsRole, user?.capabilities, user?.tenants, user?.groupAccess, user?.isPlatformAdmin])}>
        {children}
      </TenantCheckInProvider>
    </EntityPrivacyProvider>
  </CustomerSkinContext.Provider></BrowserIdentityProvider>;
}

const PHONE_QUERY = '(max-width: 767px)';

/** 'phone' at phone width, where the `mobile` brand scope switch applies. */
export function usePhoneViewport(): 'phone' | 'wide' {
  return React.useSyncExternalStore(subscribePhoneViewport, phoneViewportSnapshot, () => 'wide' as const);
}

/** Phone-width viewports honour the `mobile` brand scope switch. */
function subscribePhoneViewport(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => undefined;
  const query = window.matchMedia(PHONE_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

function phoneViewportSnapshot(): 'phone' | 'wide' {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'wide';
  return window.matchMedia(PHONE_QUERY).matches ? 'phone' : 'wide';
}
